import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import api from '../../api/api';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../hooks/useNotification';
import GoogleAuthButton from './GoogleAuthButton';
import FitText from '../common/FitText';
import BrandHomeLink from '../common/BrandHomeLink';
import './AuthPages.css';

// Design budget: how much vertical space the English copy occupies today,
// with a little headroom (see FitText) so a longer translation shrinks to
// fit instead of growing the whole rail/card taller than its English layout.
const HEADLINE_FIT = { maxFontSize: 30, minFontSize: 18, maxHeight: 110 };
const SUB_FIT = { maxFontSize: 14, minFontSize: 11, maxHeight: 68 };
const FEATURE_FIT = { maxFontSize: 13, minFontSize: 10, maxHeight: 42 };

export default function OrderTrackingLogin() {
  const { t } = useTranslation('auth');
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { show, NotificationBar } = useNotification();

  useEffect(() => {
    // A redirect here (e.g. from signup/OTP verification) can hand us a message to
    // display, since that page's own toast would be gone the instant it unmounts.
    if (location.state?.flashMessage) {
      show(location.state.flashMessage, location.state.flashType || 'success');
      // Clear it from history so a back/forward navigation doesn't re-show it.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (!form.email || !form.password) { show(t('login.fillAllFields'), 'error'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/login', form);
      if (res.data.success) {
        Cookies.set('enableInventoryLowStockAlert', res.data.enableInventoryLowStockAlert === true ? 'true' : 'false', { expires: 365 });
        login(res.data.token, res.data.user);
        const u = res.data.user;
        const profileIncomplete = !u?.storeName || !u?.phoneNumber;
        navigate(profileIncomplete ? '/products/order-tracking/profile' : '/products/order-tracking/dashboard');
      } else if (res.data.emailVerificationRequired) {
        navigate('/products/order-tracking/signup', {
          state: { verifyEmail: form.email, flashMessage: res.data.message, flashType: 'error' }
        });
      } else {
        show(res.data.message, 'error');
      }
    } catch {
      show(t('login.loginFailed'), 'error');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Sign In | EffyVenture Order Tracking</title>
        <meta name="description" content="Sign in to your EffyVenture Order Tracking account. Manage orders, inventory, customers, and invoices from one dashboard." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://order.effyventure.com/products/order-tracking/login" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://order.effyventure.com/products/order-tracking/login" />
        <meta property="og:title" content="Sign In | EffyVenture Order Tracking" />
        <meta property="og:description" content="Sign in to your EffyVenture Order Tracking account. Manage orders, inventory, customers, and invoices from one dashboard." />
      </Helmet>
      <NotificationBar />
      <div className="auth-split">
        <div className="auth-rail">
          <div className="auth-rail__content">
            <span className="eyebrow eyebrow--light">{t('login.eyebrow')}</span>
            <FitText as="h2" className="auth-rail__headline" {...HEADLINE_FIT}>{t('login.headline')}</FitText>
            <FitText as="p" className="auth-rail__sub" {...SUB_FIT}>{t('login.sub')}</FitText>
            <ul className="auth-rail__features">
              <li><span className="auth-rail__dot" /><FitText as="span" {...FEATURE_FIT}>{t('login.feature1')}</FitText></li>
              <li><span className="auth-rail__dot" /><FitText as="span" {...FEATURE_FIT}>{t('login.feature2')}</FitText></li>
              <li><span className="auth-rail__dot" /><FitText as="span" {...FEATURE_FIT}>{t('login.feature3')}</FitText></li>
            </ul>
          </div>
        </div>

        <div className="auth-card shine-rail">
          <div className="auth-card__brand">
            <BrandHomeLink className="auth-brand-logo">
              <img src="/logo/logo-icon.svg" alt="EffyVenture" className="auth-brand-logo__img" />
              <span className="auth-brand-logo__text">
                <span className="auth-brand-logo__effy">Effy</span><span className="auth-brand-logo__venture">Orders</span>
              </span>
            </BrandHomeLink>
          </div>
          <h1 className="auth-card__title">{t('login.title')}</h1>
          <p className="auth-card__subtitle">{t('login.subtitle')}</p>
          <form onSubmit={submit}>
            <div className="form-group">
              <label className="form-label">{t('login.email')}</label>
              <input className="input-field" name="email" value={form.email} onChange={handle} placeholder={t('login.emailPlaceholder')} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">{t('login.password')}</label>
              <input className="input-field" type="password" name="password" value={form.password} onChange={handle} placeholder={t('login.passwordPlaceholder')} />
            </div>
            <div className="auth-card__forgot">
              <Link to="/products/order-tracking/forgot-password">{t('login.forgotPassword')}</Link>
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? <><span className="spinner" /> {t('login.signingIn')}</> : t('login.signIn')}
            </button>
          </form>
          <GoogleAuthButton />
          <p className="auth-card__switch">
            {t('login.noAccount')} <Link to="/products/order-tracking/signup">{t('login.createOne')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
